import { useParams } from "react-router";
import { SsqSsqHeading } from "src/api/sure-api/ssq/useGetOneSsqVersion";
import { URLHandler } from "src/services/URLHandler";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { ScoreChart } from "src/components/ScoreChart/ScoreChart";
import { useOlapScoresParams } from "../../../context/OlapScores/useOlapScoresParams";
import { useGetPostScores } from "src/api/client-api/olapScores/useGetPostScores";
import { ScoreService } from "src/services/ScoreService";
import { useEffect } from "react";
import { useGetOrganizationPost } from "src/api/client-api/post/useGetOrganizationPost";
import { ExtensionPoint, EXTENSION_POINTS } from "src/components/PuckExtensions";
import { QuestionnairesList } from "src/components/QuestionnairesList/QuestionnairesList";
import { getSameLevelDimensionsChartData } from "src/utils/questionnaire";

export const Questionnaire = () => {
    const { ssqId, campaignId, scoreId } = useParams<{ ssqId: string; campaignId: string; scoreId: string }>();
    const { company, ssqVersion } = useCompanyMetadata();
    const { diagnosticScoresWithVariations, lastSelectedVariation, setEntitySsq } = useEntityScores();

    const scoresParams = useOlapScoresParams({
        scores: {
            dimensions: [[["headings"]], [["ssq_versions"], ["headings"]]],
            ids: ScoreService.getScoreIdsByLastSelectedVariation(lastSelectedVariation),
            variations: ScoreService.getVariationByLastSelectedVariation(lastSelectedVariation),
        },
    });

    const { post, isReady: isPostReady } = useGetOrganizationPost({
        companyId: company.id,
        ssqId: ssqId ? parseInt(ssqId) : undefined,
        campaignId: campaignId ? parseInt(campaignId) : undefined,
    });

    const {
        data: scores,
        isLoading: areScoresLoading,
        isRefetching: areScoresRefetching,
    } = useGetPostScores({
        postId: post?.id,
        scoresQuery: scoresParams.query,
    });

    useEffect(() => {
        scoresParams.state.setQuery({
            scores: {
                ...scoresParams.query.scores,
                ids: ScoreService.getScoreIdsByLastSelectedVariation(lastSelectedVariation),
                variations: ScoreService.getVariationByLastSelectedVariation(lastSelectedVariation),
            },
        });
    }, [lastSelectedVariation]);

    useEffect(() => {
        setEntitySsq(ssqVersion?.ssq);
    }, [ssqVersion]);

    const { data, totalScore, maxScore, specificityRate } = getSameLevelDimensionsChartData(
        scores?.olapScores[lastSelectedVariation.scoreId],
        ["headings", "ssq_versions"],
        { ssqVersion },
    );

    return (
        <>
            <ExtensionPoint extensionPoint={EXTENSION_POINTS.QUESTIONNAIRE_BEFORE} />
            <QuestionnairesList
                ssqVersion={ssqVersion}
                exportSubject={{ type: "organization", id: company.id, name: company.name }}
                post={post}
                isSsqLoading={false}
                isPostLoading={!isPostReady}
                completeQuestionnaireUrl={(ssqSsqHeading: SsqSsqHeading) =>
                    URLHandler.company.questionnaireHeading(
                        company.id,
                        parseInt(ssqId!),
                        parseInt(campaignId!),
                        scoreId ?? "",
                        ssqSsqHeading.id,
                    )
                }
                scoresSection={
                    <ScoreChart
                        scoresWithVariations={diagnosticScoresWithVariations}
                        selectedVariation={lastSelectedVariation}
                        totalScore={totalScore}
                        data={data || undefined}
                        maxScore={maxScore}
                        specificityRate={specificityRate}
                        isLoading={areScoresLoading || areScoresRefetching}
                    />
                }
            />
            <ExtensionPoint extensionPoint={EXTENSION_POINTS.QUESTIONNAIRE_AFTER} />
        </>
    );
};
